import { useEffect, useRef, useState } from "react";
import logo from "@/assets/logo.webp";
import { SocialLinks } from "@/components/Social";
import { TEL_ANTONIO, WHATSAPP_URL } from "@/lib/site";

export function SiteHeader({ home = false }: { home?: boolean }) {
  const p = home ? "" : "/";
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const servicesBtnRef = useRef<HTMLButtonElement | null>(null);
  const servicesPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const t = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(t);
  }, [open, mounted]);

  useEffect(() => {
    if (!servicesOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (servicesBtnRef.current?.contains(target)) return;
      if (servicesPanelRef.current?.contains(target)) return;
      setServicesOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setServicesOpen(false);
        servicesBtnRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [servicesOpen]);

  useEffect(() => {
    if (!open) return;

    const { overflow, paddingRight } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    const focusables = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null);

    focusables()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        toggleRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const items = [...focusables(), toggleRef.current].filter(Boolean) as HTMLElement[];
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      const inside = active && items.includes(active);
      if (!inside) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (toggleRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [open, servicesOpen]);

  const closeMenu = () => {
    setOpen(false);
    toggleRef.current?.focus();
  };

  const closeAfterNav = () => setOpen(false);

  const mainLinks = [
    { href: `${p}#lavori`, label: "Lavori" },
    { href: `${p}#metodo`, label: "Chi siamo" },
    { href: "/contattaci", label: "Contatti" },
  ];

  const serviceLinks = [
    { href: "/pulizia-lavaggio-cotto", label: "Pulizia cotto" },
    { href: "/trattamento-cotto-esterno", label: "Cotto esterno" },
    { href: "/trattamento-marmo-pietre-graniglia", label: "Marmo, pietre e graniglia" },
    { href: "/pulizia-trattamento-gres", label: "Gres e pavimenti industriali" },
    { href: "/trattamento-pavimenti-cotto-brescia", label: "Cotto a Brescia" },
  ];

  const Chevron = ({ up = false }: { up?: boolean }) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`h-4 w-4 transition-transform ${up ? "rotate-180" : ""}`}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/90 backdrop-blur">
      <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5 sm:px-5 sm:py-3 lg:flex lg:justify-between lg:gap-6">
        <a href={home ? "#top" : "/"} className="flex min-w-0 items-center gap-2 sm:gap-3">
          <img src={logo} alt="Artigiani del Cotto" width={160} height={164} className="h-9 w-auto shrink-0 sm:h-10" />
          <span className="font-display min-w-0 truncate text-sm leading-tight font-semibold tracking-tight text-sienna sm:text-base">
            Artigiani del Cotto
          </span>
        </a>

        <nav className="hidden items-center gap-7 text-sm whitespace-nowrap text-muted-foreground lg:flex">
          <div className="relative">
            <button
              ref={servicesBtnRef}
              type="button"
              onClick={() => setServicesOpen((v) => !v)}
              aria-expanded={servicesOpen}
              aria-haspopup="listbox"
              className="flex items-center gap-1 transition-colors hover:text-primary"
            >
              Servizi
              <Chevron up={servicesOpen} />
            </button>
            {servicesOpen && (
              <div
                ref={servicesPanelRef}
                className="absolute top-full left-1/2 z-50 mt-2 min-w-[220px] -translate-x-1/2 rounded-md border border-border bg-background py-2 shadow-lg"
              >
                {serviceLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className="block px-4 py-2 text-sm transition-colors hover:bg-secondary hover:text-primary"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>
          {mainLinks.map((l) => (
            <a key={l.label} href={l.href} className="transition-colors hover:text-primary">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <div className="hidden sm:flex">
            <SocialLinks />
          </div>
          <a
            href={`tel:${TEL_ANTONIO}`}
            aria-label="Chiama Artigiani del Cotto"
            title="Chiama subito"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-5 w-5">
              <path d="M6.6 10.8a15.1 15.1 0 006.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z" />
            </svg>
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Scrivi su WhatsApp"
            title="Scrivi su WhatsApp"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-5 w-5">
              <path d="M12 2a10 10 0 00-8.6 15.1L2 22l5-1.3A10 10 0 1012 2zm0 1.8a8.2 8.2 0 016.9 12.6l-.2.3.7 2.6-2.7-.7-.3.2A8.2 8.2 0 1112 3.8zm-3.7 4c-.2 0-.5 0-.7.4-.3.4-1 .9-1 2.2s1 2.6 1.1 2.8c.1.2 1.9 3 4.7 4.1 2.3.9 2.8.7 3.3.7.5 0 1.6-.6 1.9-1.3.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3l-2-1c-.3-.1-.5-.2-.7.1l-.9 1.2c-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.5-1.6-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6l-.9-2c-.2-.5-.4-.5-.6-.5h-.5z" />
            </svg>
          </a>
          <a
            href="#contatti"
            className="hidden shrink-0 rounded-xs bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna sm:inline-flex"
          >
            Preventivo
          </a>
          <button
            ref={toggleRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="menu-mobile"
            aria-label={open ? "Chiudi il menu" : "Apri il menu"}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-primary lg:hidden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="h-5 w-5">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {mounted && (
        <div
          onClick={closeMenu}
          aria-hidden="true"
          className={`fixed inset-0 z-30 bg-foreground/50 backdrop-blur-[2px] lg:hidden motion-reduce:animate-none ${
            open ? "animate-fade-in" : "animate-fade-out opacity-0"
          }`}
        />
      )}

      {mounted && (
        <nav
          id="menu-mobile"
          ref={panelRef}
          aria-label="Menu principale"
          className={`relative z-40 max-h-[calc(100dvh-3.75rem)] origin-top overflow-y-auto border-t border-border bg-background px-4 pb-4 shadow-lg lg:hidden motion-reduce:animate-none ${
            open ? "animate-fade-in" : "animate-fade-out opacity-0"
          }`}
        >
          <ul className="divide-y divide-border">
            <li>
              <button
                type="button"
                onClick={() => setServicesOpen((v) => !v)}
                aria-expanded={servicesOpen}
                aria-haspopup="listbox"
                className="flex w-full items-center justify-between py-3.5 text-base font-medium transition-colors hover:text-primary"
              >
                Servizi
                <Chevron up={servicesOpen} />
              </button>
              {servicesOpen && (
                <ul className="ml-2 mb-2 border-l border-border pl-4">
                  {serviceLinks.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        onClick={closeAfterNav}
                        className="block py-2.5 text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            {mainLinks.map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  onClick={closeAfterNav}
                  className="block py-3.5 text-base font-medium transition-colors hover:text-primary"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            href="#contatti"
            onClick={closeAfterNav}
            className="mt-4 block rounded-xs bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground"
          >
            Richiedi un preventivo gratuito
          </a>
          <div className="mt-4 flex justify-center">
            <SocialLinks />
          </div>
        </nav>
      )}
    </header>
  );
}
